import * as React from 'react';
import type { FC, PropsWithChildren, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints, styled, useTheme } from '@volkswagen-onehub/components-core';
import { ChevronLeft, ChevronRight } from '@volkswagen-onehub/icons-core';
import { css } from 'styled-components';

import { landscapeMediaMixin } from '../../hooks/use-vertical-breakpoint';

import translate from '../../utils/translate';

import { fullscreenStore } from '../../store';

interface SliderProps {
	parentRef: RefObject<HTMLDivElement>;
	activeOptionOrPickerRef?: RefObject<HTMLButtonElement>;
	showArrows?: boolean;
}

const EDGE_EPSILON = 1;
const SCROLLABLE_SLACK = 8;
const DRAG_CLICK_THRESHOLD = 5;

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const Slider: FC<PropsWithChildren<SliderProps>> = ({ children, activeOptionOrPickerRef, showArrows = false }) => {
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const { direction } = useTheme();

	const isLTR = direction == 'ltr';

	const scrollerRef = useRef<HTMLDivElement>(null);
	const [scrollability, setScrollability] = useState({
		isScrollable: false,
		canScrollStart: false,
		canScrollEnd: false
	});

	const updateScrollability = () => {
		const el = scrollerRef.current;
		if (!el) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		const position = Math.abs(el.scrollLeft);
		const isScrollable = maxScroll > SCROLLABLE_SLACK;
		const canScrollStart = isScrollable && position > EDGE_EPSILON;
		const canScrollEnd = isScrollable && position < maxScroll - SCROLLABLE_SLACK;

		setScrollability(previous =>
			previous.isScrollable === isScrollable &&
			previous.canScrollStart === canScrollStart &&
			previous.canScrollEnd === canScrollEnd
				? previous
				: { isScrollable, canScrollStart, canScrollEnd }
		);
	};

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;

		updateScrollability();

		const observer = new ResizeObserver(updateScrollability);
		observer.observe(el);

		el.addEventListener('scroll', updateScrollability, { passive: true });

		const handleResizeOrOrientationChange = () => {
			updateScrollability();
			moveToActivePicker();
		};

		window.addEventListener('resize', handleResizeOrOrientationChange);
		window.addEventListener('orientationchange', handleResizeOrOrientationChange);

		return () => {
			window.removeEventListener('resize', handleResizeOrOrientationChange);
			window.removeEventListener('orientationchange', handleResizeOrOrientationChange);
			el.removeEventListener('scroll', updateScrollability);
			observer.disconnect();
		};
	}, []);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;

		let startX = 0;
		let startScroll = 0;
		let isDragging = false;
		let hasDragged = false;

		const onPointerDown = (event: PointerEvent) => {
			if (event.pointerType !== 'mouse' || event.button !== 0) return;
			if (el.scrollWidth - el.clientWidth <= SCROLLABLE_SLACK) return;
			isDragging = true;
			hasDragged = false;
			startX = event.clientX;
			startScroll = el.scrollLeft;
		};

		const onPointerMove = (event: PointerEvent) => {
			if (!isDragging) return;
			const delta = event.clientX - startX;
			if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) hasDragged = true;
			el.scrollLeft = startScroll - delta;
		};

		const onPointerUp = () => {
			isDragging = false;
		};

		const onClickCapture = (event: MouseEvent) => {
			if (!hasDragged) return;
			hasDragged = false;
			event.preventDefault();
			event.stopPropagation();
		};

		el.addEventListener('pointerdown', onPointerDown);
		window.addEventListener('pointermove', onPointerMove);
		window.addEventListener('pointerup', onPointerUp);
		el.addEventListener('click', onClickCapture, true);

		return () => {
			el.removeEventListener('pointerdown', onPointerDown);
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('pointerup', onPointerUp);
			el.removeEventListener('click', onClickCapture, true);
		};
	}, []);

	useEffect(() => {
		updateScrollability();
	});

	useEffect(() => {
		moveToActivePicker();
	}, [activeOptionOrPickerRef]);

	function moveToActivePicker() {
		const el = scrollerRef.current;
		const active = activeOptionOrPickerRef?.current;
		if (!el || !active) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		if (maxScroll <= SCROLLABLE_SLACK) return;

		const activeCenter = active.offsetLeft + active.offsetWidth / 2;
		const centered = Math.max(0, Math.min(maxScroll, activeCenter - el.clientWidth / 2));
		const target = isLTR ? centered : centered - maxScroll;

		el.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
	}

	function scrollByPage(towardsStart: boolean) {
		const el = scrollerRef.current;
		if (!el) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		const page = el.clientWidth * 0.8;
		const delta = page * (towardsStart ? -1 : 1) * (isLTR ? 1 : -1);
		const min = isLTR ? 0 : -maxScroll;
		const max = isLTR ? maxScroll : 0;
		const target = Math.max(min, Math.min(max, el.scrollLeft + delta));

		el.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
	}

	const showStartArrow = showArrows && scrollability.canScrollStart;
	const showEndArrow = showArrows && scrollability.canScrollEnd;

	const previousLabel = translate('global.ariaLabel.scrollOptionsPrevious');
	const nextLabel = translate('global.ariaLabel.scrollOptionsNext');

	const inertWhenHidden = (visible: boolean) => (visible ? {} : ({ inert: '' } as Record<string, string>));

	return (
		<Wrapper isFullscreenMode={isFullscreenMode}>
			<FadeZone $edge="start" $visible={showStartArrow} $isFullscreenMode={isFullscreenMode}>
				<ArrowButton
					type="button"
					$edge="start"
					$visible={showStartArrow}
					{...inertWhenHidden(showStartArrow)}
					aria-label={isLTR ? previousLabel : nextLabel}
					onClick={e => {
						e.stopPropagation();
						scrollByPage(true);
					}}
				>
					<ChevronLeft aria-hidden variant="small" />
				</ArrowButton>
			</FadeZone>
			<Scroller ref={scrollerRef} $isScrollable={scrollability.isScrollable} onClick={e => e.stopPropagation()}>
				{children}
			</Scroller>
			<FadeZone $edge="end" $visible={showEndArrow} $isFullscreenMode={isFullscreenMode}>
				<ArrowButton
					type="button"
					$edge="end"
					$visible={showEndArrow}
					{...inertWhenHidden(showEndArrow)}
					aria-label={isLTR ? nextLabel : previousLabel}
					onClick={e => {
						e.stopPropagation();
						scrollByPage(false);
					}}
				>
					<ChevronRight aria-hidden variant="small" />
				</ArrowButton>
			</FadeZone>
		</Wrapper>
	);
};

export default Slider;

const Wrapper = styled.div<{ isFullscreenMode: boolean }>`
	position: relative;
	overflow: hidden;

	${props => props.isFullscreenMode && DoubleSliderMixin()} @media ${landscapeMediaMixin} {
		${() => DoubleSliderMixin()}
	}
`;

const Scroller = styled.div<{ $isScrollable: boolean }>`
	display: flex;
	flex-wrap: nowrap;
	gap: 8px;

	padding: 8px 0 8px 4px;

	overflow-x: ${({ $isScrollable }) => ($isScrollable ? 'auto' : 'hidden')};
	overscroll-behavior-x: contain;
	user-select: none;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}

	& > * {
		flex-shrink: 0;
	}

	&::after {
		content: '';
		flex: 0 0 1px;
	}
`;

const FadeZone = styled.div<{ $edge: 'start' | 'end'; $visible: boolean; $isFullscreenMode: boolean }>`
	position: absolute;
	top: 0;
	bottom: 0;
	${({ $edge }) => ($edge === 'start' ? 'inset-inline-start: 0;' : 'inset-inline-end: 0;')}
	z-index: 2;

	width: 56px;
	pointer-events: none;

	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	transition: opacity 150ms ease;

	background: ${({ $edge, theme, $isFullscreenMode }) =>
		$isFullscreenMode
			? 'none'
			: `linear-gradient(to ${
					($edge === 'start') === (theme.direction === 'ltr') ? 'right' : 'left'
				}, #f6f5f2, rgba(246, 245, 242, 0))`};
`;

const ArrowButton = styled.button<{ $edge: 'start' | 'end'; $visible: boolean }>`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	${({ $edge }) => ($edge === 'start' ? 'inset-inline-start: 2px;' : 'inset-inline-end: 2px;')}

	display: flex;
	align-items: center;
	justify-content: center;

	width: 28px;
	height: 28px;
	padding: 0;
	border: none;
	border-radius: 50%;
	cursor: pointer;

	background: #293043;
	color: #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

	& svg {
		width: 12px;
		height: 12px;
	}

	pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

	@media (max-width: ${Breakpoints.b960 - 1}px) {
		width: 32px;
		height: 32px;
	}
`;

const DoubleSliderMixin = () => {
	const masking = css`
		mask-image: linear-gradient(
			to right,
			transparent 0px,
			black ${({ theme }) => theme.size.static150},
			black calc(100% - ${({ theme }) => theme.size.static150}),
			transparent 100%
		);
	`;

	return css`
		${masking}
	`;
};
